import React, { createContext, useContext, useState, useEffect } from 'react';
import airtableService from '../utils/airtable';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored session
    const storedUser = localStorage.getItem('fw3_upload_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('fw3_upload_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await airtableService.authenticateUser(username, password);

      if (response.success && response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        localStorage.setItem('fw3_upload_user', JSON.stringify(response.data));
        return true;
      }

      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('fw3_upload_user');
  };

  const canUpload = () => {
    // Must be authenticated to upload
    if (!isAuthenticated || !user) {
      return false;
    }

    // Admin and Content Manager can upload
    if (user.userRole === 'Admin' || user.userRole === 'Content Manager') {
      return true;
    }

    return false;
  };

  /**
   * Check if user can access a file based on its access level
   * Implements 4-tier access control: Public, Authenticated, Private, Admin Only
   * @param {string} requiredLevel - The access level required (Public, Authenticated, Private, Admin Only)
   * @returns {boolean} - True if user has access
   */
  const checkAccess = (requiredLevel) => {
    // Public files are always accessible
    if (requiredLevel === 'Public') {
      return true;
    }

    // Not authenticated - only Public files allowed
    if (!isAuthenticated || !user) {
      return false;
    }

    // Admin can access everything
    if (user.userRole === 'Admin') {
      return true;
    }

    // Admin Only files require Admin role (already checked above)
    if (requiredLevel === 'Admin Only') {
      return false;
    }

    // Private level - Content Managers can access
    if (requiredLevel === 'Private') {
      return user.userRole === 'Content Manager';
    }

    // Authenticated level - any authenticated user
    if (requiredLevel === 'Authenticated') {
      return true;
    }

    return false;
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    canUpload,
    checkAccess
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
